import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// see https://codesandbox.io/p/sandbox/multi-range-slider-react-js-forked-4uq1uo
// ---Third-party imports
import React from 'react';
import clsx from 'clsx';
// ---Framework imports
import { callIfFunc } from 'ldash';
import { useDebounce, useCallback, useEffect, useState, useRef } from 'uix/hooks';
// ---Local imports
import styles from './multiRangeSlider.css';
const MultiRangeSlider = ({ min, max, defaultMin, defaultMax, onChange, style })=>{
    const [minVal, setMinVal] = useState(defaultMin ?? min);
    const [maxVal, setMaxVal] = useState(defaultMax ?? max);
    const minValRef = useRef(defaultMin ?? min);
    const maxValRef = useRef(defaultMax ?? max);
    const range = useRef(null);
    // Convert to percentage
    const getPercent = useCallback((value)=>Math.round((value - min) / (max - min) * 100), [
        min,
        max
    ]);
    // Set width of the range to decrease from the left side
    useEffect(()=>{
        const minPercent = getPercent(minVal);
        const maxPercent = getPercent(maxValRef.current);
        if (range.current) {
            range.current.style.left = `${minPercent}%`;
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [
        minVal,
        getPercent
    ]);
    // Set width of the range to decrease from the right side
    useEffect(()=>{
        const minPercent = getPercent(minValRef.current);
        const maxPercent = getPercent(maxVal);
        if (range.current) {
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [
        maxVal,
        getPercent
    ]);
    let [debouncedMin] = useDebounce(minVal, 300, {
        leading: false
    });
    let [debouncedMax] = useDebounce(maxVal, 300, {
        leading: false
    });
    // Get min and max values when their state changes
    useEffect(()=>{
        callIfFunc(onChange, {
            min: debouncedMin,
            max: debouncedMax
        });
    }, [
        debouncedMin,
        debouncedMax
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.multiRangeSlider,
        style: style,
        children: [
            /*#__PURE__*/ _jsx("input", {
                type: "range",
                min: min,
                max: max,
                value: minVal,
                onChange: (event)=>{
                    const value = Math.min(Number(event.target.value), maxVal - 1);
                    setMinVal(value);
                    minValRef.current = value;
                },
                className: clsx(styles.thumb, styles.thumb_left),
                style: {
                    zIndex: minVal > max - 100 && '5'
                }
            }),
            /*#__PURE__*/ _jsx("input", {
                type: "range",
                min: min,
                max: max,
                value: maxVal,
                onChange: (event)=>{
                    const value = Math.max(Number(event.target.value), minVal + 1);
                    setMaxVal(value);
                    maxValRef.current = value;
                },
                className: clsx(styles.thumb, styles.thumb_right)
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.slider,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.slider_track
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        ref: range,
                        className: styles.slider_range
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.slider_left_value,
                        children: minVal
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.slider_right_value,
                        children: maxVal
                    })
                ]
            })
        ]
    });
};
export { MultiRangeSlider };
